<template>
    <div class="documents_card">
        <div class="card_toolbar">
            <div class="search_wrap">
                <SearchIcon class="search_icon" />
                <input
                    class="search_input"
                    type="search"
                    :value="search"
                    placeholder="Buscar por número de documento"
                    @input="onSearchInput" />
            </div>
        </div>

        <div class="table_container" v-if="filteredDocuments.length > 0">
            <table class="documents_table">
                <thead>
                    <tr>
                        <th class="col_date">Fecha Emisión</th>
                        <th class="col_doctype">Tipo de documento</th>
                        <th class="col_docnum">N° Doc.</th>
                        <th class="col_amount_prorated">Monto prorrateado</th>
                        <th class="col_amount_total">Monto total</th>
                        <th class="col_account">Cuenta</th>
                        <th class="col_categories">Categorías</th>
                        <th class="col_action"></th>
                    </tr>
                </thead>
                <tbody>
                    <template v-for="doc in filteredDocuments">
                        <tr :key="`row-${doc.td_id}`"
                            class="doc_row"
                            :class="{ 'is-cancelled': doc.cancelled, 'is-expanded': expandedDocId === doc.td_id }">
                            <td class="col_date">{{ formatDate(doc.emitted_at) }}</td>
                            <td class="col_doctype">{{ doc.doc_type }}</td>
                            <td class="col_docnum">{{ doc.doc_number }}</td>
                            <td class="col_amount_prorated">{{ formatMoney(doc.amount) }}</td>
                            <td class="col_amount_total">{{ formatMoney(totalAmount(doc)) }}</td>
                            <td class="col_account">
                                <span v-if="doc.cancelled" class="status_pill status_cancelled">
                                    Anulado
                                </span>
                                <template v-else-if="doc.accounts && doc.accounts.length">
                                    <span v-for="(acc, idx) in uniqueLevel3s(doc.accounts)"
                                        :key="`acc-${doc.td_id}-${idx}`"
                                        class="account_pill">
                                        {{ acc }}
                                    </span>
                                </template>
                                <span v-else class="status_pill status_unclassified">
                                    Sin Clasificar
                                </span>
                            </td>
                            <td class="col_categories">
                                <div v-for="(items, type, typeIdx) in doc.attribute"
                                    :key="`attr-${doc.td_id}-${type}`"
                                    class="category_group">
                                    <span class="category_type">{{ type }}:</span>
                                    <span v-for="(attr, attrIdx) in dedupeAttributes(items)"
                                        :key="`attr-${doc.td_id}-${type}-${attrIdx}`"
                                        :class="['category_pill', `category_pill_tier_${typeIdx % 3}`]">
                                        {{ attr.name }}
                                    </span>
                                </div>
                            </td>
                            <td class="col_action">
                                <button v-if="!doc.cancelled"
                                    class="edit_btn"
                                    :class="{ 'is-active': expandedDocId === doc.td_id }"
                                    aria-label="Clasificar documento"
                                    @click="toggleExpand(doc)">
                                    <EditPencil class="edit_icon" />
                                </button>
                            </td>
                        </tr>
                        <tr v-if="expandedDocId === doc.td_id"
                            :key="`expand-${doc.td_id}`"
                            class="doc_expand_row">
                            <td :colspan="columnCount" class="doc_expand_cell">
                                <div class="tree_container">
                                    <TreeClassificator
                                        :key="treeKey"
                                        :classifiedAccounts="selectedClassifiedAccounts"
                                        :accounts="accountPlan"
                                        :types="categories"
                                        :classifiedAttributes="selectedClassifiedAttributes"
                                        :allAttributes="allAttributes"
                                        :documentType="counterpart && counterpart.pay_or_charge"
                                        :contraparteId="counterpart && counterpart.ci_id"
                                        :documentsIds="[doc.td_id]"
                                        :money="totalAmount(doc)"
                                        :obligationName="counterpart && counterpart.obligation_name"
                                        @closeTree="collapseTree"
                                        @reloadDocs="onTreeReload" />
                                </div>
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>

        <p v-else-if="search" class="empty_state">
            Sin resultados para "{{ search }}".
        </p>
        <p v-else class="empty_state">
            Este cliente no tiene documentos en el mes seleccionado.
        </p>
    </div>
</template>

<script>
import moment from "moment";

import { get_prorates_x_company_involved } from "@/api/carteraAnalista";
import { parseToMoney } from "@/utils/currency";
import { consoleHandlerError } from "@/utils/message";

import EditPencil from "@/components/svg/EditPencil.vue";
import SearchIcon from "@/components/svg/SearchIcon.vue";

// TreeClassificator se carga async para evitar el costo de su import-chain
// (ClassificationBox + SelectComponent + tree de svg) al evaluar este módulo.
// La diferencia es importante para los tests que sólo cargan DashboardReports
// — sin async, traer este árbol al primer render hace que TreeClassificator
// invoque registros globales que rompen los mocks de $route del runner Jest.
const TreeClassificator = () => import("@/components/classification/TreeClassificator.vue");

export default {
    name: "DocumentsDetailCard",

    components: { TreeClassificator, EditPencil, SearchIcon },

    props: {
        /** Lista filtrada por search vía v-model implícito (search + documents). */
        filteredDocuments: { type: Array, required: true },
        /** Counterpart cargado por openClientDetail (incluye ci_id, pay_or_charge, obligation_name). */
        counterpart: { type: Object, default: null },
        /** Search query (v-model con el store del padre — el padre hace bridge). */
        search: { type: String, default: "" },
        /** Categorías globales del classification store (tipos: tiendas, marcas, ...). */
        categories: { type: Array, default: () => [] },
    },

    data() {
        return {
            expandedDocId: null,
            accountPlan: [],
            selectedClassifiedAccounts: [],
            selectedClassifiedAttributes: [],
            allAttributes: [],
            // Forzar re-mount del TreeClassificator cuando cambian los datos
            // pero el documento expandido es el mismo (post reload). El
            // componente lee algunas props sólo en `mounted`/`watch`, así
            // que el key es lo único confiable.
            treeKey: 0,
            // Cantidad de columnas del thead. Lo usa el `<td colspan>` del
            // expand row — declararlo acá evita que un cambio de columnas
            // rompa silenciosamente el ancho de la fila expandida.
            columnCount: 8,
        };
    },

    methods: {
        onSearchInput(event) {
            this.$emit("update-search", event.target.value);
        },

        formatDate(value) {
            if (!value) return "";
            return moment(value).format("DD-MM-YYYY");
        },

        formatMoney(value) {
            if (value == null) return "";
            return parseToMoney(value);
        },

        // El backend devuelve `amount` ya prorrateado al mes y conserva el
        // monto facturado original en `original_amount`. Si esa key falta
        // (datos legacy o algún edge case), caemos al prorrateado mismo —
        // peor caso ambas columnas muestran lo mismo, mejor que un blank.
        totalAmount(doc) {
            if (doc == null) return 0;
            return doc.original_amount != null ? doc.original_amount : doc.amount;
        },

        // `doc.accounts` viene como [{ level_3, level_2, level_1, percentage, ... }]
        // con posibles duplicados por prorrateo. La tabla muestra cada level_3
        // una sola vez (la card del usuario es visualizar, no inspeccionar el
        // detalle de splits — eso vive en el TreeClassificator expandido).
        uniqueLevel3s(accounts) {
            const seen = new Set();
            const result = [];
            for (const acc of accounts) {
                if (!acc.level_3 || seen.has(acc.level_3)) continue;
                seen.add(acc.level_3);
                result.push(acc.level_3);
            }
            return result;
        },

        // Atributos en `doc.attribute[type]` también pueden duplicarse por
        // splits (Tienda 1 al 80% y 20% en dos prorrateos). El chip es
        // representación binaria — el cuántos detalles ya viven en el tree.
        dedupeAttributes(items) {
            const seen = new Set();
            return (items || []).filter((it) => {
                if (!it || seen.has(it.name)) return false;
                seen.add(it.name);
                return true;
            });
        },

        async toggleExpand(doc) {
            if (this.expandedDocId === doc.td_id) {
                this.collapseTree();
                return;
            }
            if (!this.counterpart) return;

            // Cargar el árbol del prorrateo por documento. Pasamos el td_id
            // explícito → el endpoint devuelve la clasificación específica
            // del doc (si existe) o vacío para que el árbol arranque desde
            // la plantilla por contraparte.
            try {
                const { data } = await get_prorates_x_company_involved(
                    this.counterpart.ci_id,
                    doc.td_id,
                    this.counterpart.pay_or_charge,
                    this.counterpart.obligation_name,
                );
                this.initTreeData(data);
                this.expandedDocId = doc.td_id;
                this.treeKey += 1;
            } catch (error) {
                consoleHandlerError({ error, prefix: "[clientDetail] expand-tree", silent: true });
            }
        },

        initTreeData(data) {
            if (!data) return;
            this.accountPlan = data.account_plan_ordered || [];
            this.allAttributes = data.all_attributes || [];
            this.selectedClassifiedAttributes = data.prorates_x_attribute
                ? [...data.prorates_x_attribute]
                : [];
            const proratesAccount = data.prorate_x_account || [];
            this.selectedClassifiedAccounts = proratesAccount.map((pxa) => ({
                id: Number(pxa.id),
                percentage: pxa.percentage,
                account: Number(pxa.classified_account),
            }));
        },

        collapseTree() {
            this.expandedDocId = null;
        },

        onTreeReload() {
            // Tras guardar el árbol, pedimos al padre que recargue documents.
            // Mantenemos el row expandido para que el feedback sea inmediato
            // (el panel actualiza pills al volver). El padre debe re-emit
            // el set actualizado.
            this.$emit("reload-documents");
            this.treeKey += 1;
        },
    },
};
</script>

<style scoped lang="scss">
.documents_card {
    background: var(--report-bg);
    border: 1px solid var(--report-border);
    border-radius: 12px;
    box-shadow: 0 1px 2px rgba(10, 13, 20, 0.03);
    overflow: hidden;
}

.card_toolbar {
    padding: 16px 20px;
    border-bottom: 1px solid var(--report-border);
    display: flex;
    align-items: center;
    gap: 16px;
}

.search_wrap {
    position: relative;
    flex: 1;
    max-width: 360px;
}

.search_icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    width: 16px;
    height: 16px;
    color: var(--report-text-muted-soft);
    pointer-events: none;
}

.search_input {
    width: 100%;
    height: 40px;
    padding: 0 12px 0 38px;
    border: 1px solid var(--report-border);
    border-radius: 999px;
    background: var(--report-bg);
    font-size: 14px;
    color: var(--report-text);
    box-sizing: border-box;

    &::placeholder {
        color: var(--report-text-muted-soft);
    }

    &:focus {
        outline: none;
        border-color: var(--report-accent-text);
        box-shadow: 0 0 0 3px var(--report-accent-bg);
    }
}

.table_container {
    width: 100%;
}

.documents_table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    // Centramos sólo las celdas de la grilla de datos (header + filas de
    // documento). El expand row tiene su propio `<td>` con contenido libre y
    // mantiene la alineación por defecto.
    thead th,
    .doc_row > td {
        text-align: center;
    }

    th {
        font-size: 12px;
        font-weight: 700;
        color: var(--report-text-muted-strong);
        padding: 12px 16px;
        border-bottom: 1px solid var(--report-border);
        background: var(--report-bg);
        white-space: nowrap;
    }

    td {
        padding: 14px 16px;
        border-bottom: 1px solid var(--report-divider-soft);
        color: var(--report-text);
        vertical-align: middle;
    }
}

.doc_row {
    transition: background-color 120ms ease;

    &:hover {
        background: var(--report-surface);
    }

    &.is-expanded {
        background: var(--report-surface);
    }

    &.is-cancelled {
        opacity: 0.6;
    }
}

.col_doctype,
.col_account,
.col_categories {
    min-width: 120px;
}

// Anchos por columna. La alineación horizontal la dicta el bloque `th, td`
// con `text-align: center` — todas las celdas (incluyendo headers) quedan
// centradas para una lectura simétrica fila a fila.
.col_docnum {
    width: 70px;
}

// Dos columnas de monto: prorrateado al mes (la que matchea el ranking) y
// total facturado. Mismo ancho para que el lector las compare fila por fila
// sin saltos visuales. El prorrateado lleva color de texto principal porque
// es la magnitud que da el contexto del análisis; el total queda en muted
// como referencia secundaria.
.col_amount_prorated,
.col_amount_total {
    font-weight: 500;
    white-space: nowrap;
    width: 130px;
}

.col_amount_prorated {
    color: var(--report-text);
}

.col_amount_total {
    color: var(--report-text-muted-strong);
}

.col_action {
    width: 40px;
}

%pill_inline {
    display: inline-flex;
    align-items: center;
    padding: 2px 10px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 500;
    border: 1px solid transparent;
    white-space: nowrap;
}

.account_pill {
    @extend %pill_inline;
    background: var(--report-bg);
    color: var(--report-text-muted-strong);
    border-color: var(--report-border);
    margin-right: 4px;
}

.status_pill {
    @extend %pill_inline;
}

.status_unclassified {
    background: var(--report-surface);
    color: var(--report-text-muted-strong);
    border-color: var(--report-border);
}

.status_cancelled {
    background: #fef2f2;
    color: #b42318;
    border-color: #fecaca;
}

.category_group {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-wrap: wrap;
    gap: 4px;
    margin-bottom: 4px;

    &:last-child {
        margin-bottom: 0;
    }
}

.category_type {
    font-size: 12px;
    color: var(--report-text-muted);
    margin-right: 4px;
    text-transform: capitalize;
}

.category_pill {
    @extend %pill_inline;
    font-size: 12px;
    padding: 1px 8px;
}

// Tiers cromáticos para diferenciar grupos de atributos cuando hay varios
// types (tiendas vs marcas). Reutilizamos los tres chart-segment como anclas
// de identidad — coherente con la familia visual del feature.
.category_pill_tier_0 {
    background: #eff6ff;
    color: #1e40af;
    border-color: #bfdbfe;
}

.category_pill_tier_1 {
    background: #f5f3ff;
    color: #5b21b6;
    border-color: #ddd6fe;
}

.category_pill_tier_2 {
    background: #ecfdf3;
    color: #067647;
    border-color: #abefc6;
}

.edit_btn {
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 4px;
    border-radius: 6px;
    color: var(--report-text-muted-strong);
    transition: background-color 120ms ease, color 120ms ease;

    &:hover {
        background: var(--report-surface);
        color: var(--report-text);
    }

    &.is-active {
        background: var(--report-accent-bg);
        color: var(--report-accent-text);
    }
}

.edit_icon {
    width: 16px;
    height: 16px;
}

.doc_expand_row {
    background: var(--report-surface);
}

.doc_expand_cell {
    padding: 0 !important;
    border-bottom: 1px solid var(--report-border);
}

.tree_container {
    padding: 20px;
    background: var(--report-surface);
}

.empty_state {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--report-text-muted);
    font-size: 13px;
    margin: 0;
    padding: 32px;
}
</style>
